import {
  getRobotStateApi,
  getMentionsApi,
  getTasksApi,
  getOrdersApi,
  getArchivedOrdersApi,
  getOrdersTabsApi,
  getOrdersProviderOptionsApi,
  getOrdersIssueTypesApi,
  markOrderReadApi,
  archiveOrderApi,
  batchArchiveOrdersApi,
  batchUnarchiveOrdersApi,
  getGuardianSessionsApi,
  getGuardianMessagesApi,
  sendGuardianMessageApi,
  archiveGuardianSessionApi,
  mapSessionToItem,
  mapMessageToChatItem,
  batchReadOrdersApi,
} from '@/api/notifications';
import {
  getPendingApprovalsApi,
  approveApprovalApi,
  rejectApprovalApi,
  mapApprovalToItem,
} from '@/api/approvals';
import { markNoteAsReadApi, getRepliesApi, createReplyApi } from '@/api/internalNotes';
import { notify } from '@/utils/notify';

const notificationsModule = {
  namespaced: true,

  state: {
    robotMessage: {
      greeting: '',
      counts: { automations: 0, mentions: 0, tasks: 0, orders: 0 },
    },
    activeTab: 'automations',

    automations: { items: [], completedItems: [], loading: false, error: null, expandedCardId: null, messagesLoading: false, sendingMessage: false, totalCount: null },
    approvals: { items: [], approvedItems: [], loading: false, error: null, actionInFlight: null },
    mentions: { chat: [], internalNotes: [], loading: false, error: null, unreadCount: null, page: 1, perPage: 10, total: 0 },
    tasks: { items: [], loading: false, error: null, filter: 'all', page: 1, perPage: 10, total: 0, openedTaskCount: null },
    orders: {
      items: [],
      loading: false,
      error: null,
      subTab: 'issues',
      isArchived: false,
      type: 'all',
      provider: 'all',
      providerOptions: [{ value: 'all', label: 'All' }],
      providerOptionsLoaded: false,
      issueTypeOptions: [{ value: 'all', label: 'All' }],
      issueTypeOptionsLoaded: false,
      patientId: '',
      limit: 10,
      total: 0,
      tabsBadge: {
        issues: { unread: 0, total: 0 },
        reports: { unread: 0, total: 0 },
        operations: { unread: 0, total: 0 },
      },
      ordersBadge: { unread: 0, total: 0 },
      partialErrors: [],
      hasMore: false,
      nextCursor: '',
      cursorHistory: [''],
      cursorIndex: 0,
      pageUnread: 0,
    },
  },

  getters: {
    badgeCounts(state) {
      const automationsCount = state.automations.totalCount ?? 0;
      const approvalsCount = state.approvals.items.length;
      const mentionsCount = state.mentions.unreadCount ?? 0;
      const tasksCount = state.tasks.openedTaskCount ?? 0;
      const ordersUnread = state.orders?.ordersBadge?.unread ?? state.robotMessage.counts.orders;
      return {
        automations: automationsCount + approvalsCount,
        mentions: mentionsCount,
        tasks: tasksCount,
        orders: ordersUnread,
      };
    },
  },

  mutations: {
    SET_ROBOT_MESSAGE(state, payload) {
      state.robotMessage = payload;
    },
    SET_ACTIVE_TAB(state, tab) {
      state.activeTab = tab;
    },

    // Automations
    SET_AUTOMATIONS_LOADING(state, val) { state.automations.loading = val; },
    SET_AUTOMATIONS_ERROR(state, val) { state.automations.error = val; },
    SET_AUTOMATIONS_DATA(state, { items, completedItems }) {
      state.automations.items = items;
      state.automations.completedItems = completedItems;
    },
    SET_AUTOMATIONS_TOTAL_COUNT(state, count) { state.automations.totalCount = count; },
    SET_EXPANDED_CARD(state, id) {
      state.automations.expandedCardId = state.automations.expandedCardId === id ? null : id;
    },
    SET_AUTOMATIONS_MESSAGES_LOADING(state, val) { state.automations.messagesLoading = val; },
    SET_AUTOMATIONS_SENDING(state, val) { state.automations.sendingMessage = val; },
    SET_ITEM_CHAT_HISTORY(state, { sessionId, chatHistory }) {
      const item = state.automations.items.find(i => i.id === sessionId)
        || state.automations.completedItems.find(i => i.id === sessionId);
      if (item) {
        item.chatHistory = chatHistory;
      }
    },
    APPEND_CHAT_MESSAGE(state, { sessionId, message }) {
      const item = state.automations.items.find(i => i.id === sessionId);
      if (item && item.chatHistory) {
        // Deduplicate: skip if the last message has the same type and text
        const last = item.chatHistory[item.chatHistory.length - 1];
        if (last && last.type === message.type && last.text === message.text) return;
        item.chatHistory.push(message);
      }
    },
    ADD_AUTOMATION_ITEM(state, item) {
      state.automations.items.unshift(item);
    },
    MOVE_TO_COMPLETED(state, { sessionId }) {
      const idx = state.automations.items.findIndex(i => i.id === sessionId);
      if (idx !== -1) {
        const [item] = state.automations.items.splice(idx, 1);
        item.priority = 'complete';
        state.automations.completedItems.unshift(item);
      }
    },
    REMOVE_AUTOMATION_ITEM(state, sessionId) {
      state.automations.items = state.automations.items.filter(i => i.id !== sessionId);
      state.automations.completedItems = state.automations.completedItems.filter(i => i.id !== sessionId);
    },
    UPDATE_ITEM_DESCRIPTION(state, { sessionId, description }) {
      const item = state.automations.items.find(i => i.id === sessionId);
      if (item) {
        item.description = description;
      }
    },

    // Approvals
    SET_APPROVALS_LOADING(state, val) { state.approvals.loading = val; },
    SET_APPROVALS_ERROR(state, val) { state.approvals.error = val; },
    SET_APPROVALS_ITEMS(state, items) { state.approvals.items = items; },
    SET_APPROVAL_ACTION_IN_FLIGHT(state, val) { state.approvals.actionInFlight = val; },
    MOVE_APPROVAL_TO_APPROVED(state, { approvalExecutionId, approvedTitle }) {
      const idx = state.approvals.items.findIndex(i => i.id === approvalExecutionId);
      if (idx !== -1) {
        const [item] = state.approvals.items.splice(idx, 1);
        item.status = 'approved';
        if (approvedTitle) item.approvedTitle = approvedTitle;
        state.approvals.approvedItems.unshift(item);
      }
    },
    REMOVE_APPROVAL_ITEM(state, approvalExecutionId) {
      state.approvals.items = state.approvals.items.filter(i => i.id !== approvalExecutionId);
      state.approvals.approvedItems = state.approvals.approvedItems.filter(i => i.id !== approvalExecutionId);
    },

    // Mentions
    SET_MENTIONS_LOADING(state, val) { state.mentions.loading = val; },
    SET_MENTIONS_ERROR(state, val) { state.mentions.error = val; },
    SET_MENTIONS_DATA(state, { chat, internalNotes }) {
      state.mentions.chat = chat;
      state.mentions.internalNotes = internalNotes;
    },
    MARK_MENTION_READ(state, noteId) {
      const item = state.mentions.internalNotes.find(n => n.id === noteId);
      if (item) item.isNew = false;
    },
    SET_MENTIONS_UNREAD_COUNT(state, count) { state.mentions.unreadCount = count; },
    SET_MENTIONS_PAGE(state, page) { state.mentions.page = page; },
    SET_MENTIONS_PER_PAGE(state, perPage) { state.mentions.perPage = perPage; },
    SET_MENTIONS_TOTAL(state, total) { state.mentions.total = total; },

    // Tasks
    SET_TASKS_LOADING(state, val) { state.tasks.loading = val; },
    SET_TASKS_ERROR(state, val) { state.tasks.error = val; },
    SET_TASKS_DATA(state, { items, total }) {
      state.tasks.items = items;
      state.tasks.total = total;
    },
    SET_TASKS_FILTER(state, filter) { state.tasks.filter = filter; },
    SET_TASKS_PAGE(state, page) { state.tasks.page = page; },
    SET_TASKS_PER_PAGE(state, perPage) { state.tasks.perPage = perPage; },
    SET_TASKS_OPENED_COUNT(state, count) { state.tasks.openedTaskCount = count; },

    // Orders
    SET_ORDERS_LOADING(state, val) { state.orders.loading = val; },
    SET_ORDERS_ERROR(state, val) { state.orders.error = val; },
    SET_ORDERS_DATA(state, { items, total }) {
      state.orders.items = items;
      state.orders.total = total;
    },
    SET_ORDERS_SUB_TAB(state, subTab) { state.orders.subTab = subTab; },
    SET_ORDERS_IS_ARCHIVED(state, val) { state.orders.isArchived = Boolean(val); },
    SET_ORDERS_TYPE(state, val) { state.orders.type = val || 'all'; },
    SET_ORDERS_PROVIDER(state, val) { state.orders.provider = val || 'all'; },
    SET_ORDERS_PROVIDER_OPTIONS(state, options) {
      state.orders.providerOptions = options;
      state.orders.providerOptionsLoaded = true;
      const hasSelectedProvider = options.some(option => option.value === state.orders.provider);
      if (!hasSelectedProvider) {
        state.orders.provider = 'all';
      }
    },
    SET_ORDERS_ISSUE_TYPE_OPTIONS(state, options) {
      state.orders.issueTypeOptions = options;
      state.orders.issueTypeOptionsLoaded = true;
    },
    SET_ORDERS_PATIENT_ID(state, val) { state.orders.patientId = val; },
    SET_ORDERS_LIMIT(state, limit) { state.orders.limit = limit; },
    SET_ORDERS_TABS_BADGE(state, badgeMap) {
      state.orders.tabsBadge = {
        issues: badgeMap.issues || { unread: 0, total: 0 },
        reports: badgeMap.reports || { unread: 0, total: 0 },
        operations: badgeMap.operations || { unread: 0, total: 0 },
      };
    },
    SET_ORDERS_TOP_BADGE(state, badge) {
      state.orders.ordersBadge = badge || { unread: 0, total: 0 };
    },
    SET_ORDERS_PARTIAL_ERRORS(state, partialErrors) {
      state.orders.partialErrors = partialErrors || [];
    },
    SET_ORDERS_HAS_MORE(state, val) { state.orders.hasMore = Boolean(val); },
    SET_ORDERS_NEXT_CURSOR(state, cursor) { state.orders.nextCursor = cursor || ''; },
    RESET_ORDERS_CURSOR(state) {
      state.orders.cursorHistory = [''];
      state.orders.cursorIndex = 0;
      state.orders.nextCursor = '';
      state.orders.hasMore = false;
    },
    PUSH_ORDERS_CURSOR(state, cursor) {
      if (!cursor) return;
      const targetIndex = state.orders.cursorIndex + 1;
      if (state.orders.cursorHistory[targetIndex] !== cursor) {
        state.orders.cursorHistory = state.orders.cursorHistory.slice(0, targetIndex);
        state.orders.cursorHistory.push(cursor);
      }
    },
    SET_ORDERS_CURSOR_INDEX(state, index) {
      if (index < 0) return;
      if (index >= state.orders.cursorHistory.length) return;
      state.orders.cursorIndex = index;
    },
    MARK_ORDER_ITEM_READ(state, id) {
      state.orders.items = state.orders.items.map(item => {
        if (item.id !== id) return item;
        return { ...item, read: true };
      });
    },
    SET_ORDERS_PAGE_UNREAD(state, count) { state.orders.pageUnread = count || 0; },
    REMOVE_ORDER_ITEM(state, id) {
      state.orders.items = state.orders.items.filter(item => item.id !== id);
    },
  },

  actions: {
    async fetchRobotState({ commit }) {
      try {
        const { data } = await getRobotStateApi();
        commit('SET_ROBOT_MESSAGE', data);
      } catch (e) {
        console.error('fetchRobotState error:', e);
      }
    },

    async fetchAutomations({ commit, rootState }) {
      commit('SET_AUTOMATIONS_LOADING', true);
      commit('SET_AUTOMATIONS_ERROR', null);
      try {
        const clinicId = rootState.login?.clinic_id;
        const { data } = await getGuardianSessionsApi(clinicId);
        const sessions = data?.sessions || [];
        const activeItems = [];
        const completedItems = [];
        for (const s of sessions) {
          const item = mapSessionToItem(s);
          if (s.status === 'resolved') {
            item.priority = 'complete';
            completedItems.push(item);
          } else {
            activeItems.push(item);
          }
        }
        commit('SET_AUTOMATIONS_DATA', { items: activeItems, completedItems });
        commit('SET_AUTOMATIONS_TOTAL_COUNT', activeItems.length);
      } catch (e) {
        console.error('fetchAutomations error:', e);
        commit('SET_AUTOMATIONS_ERROR', 'Failed to load automations. Please try again.');
      } finally {
        commit('SET_AUTOMATIONS_LOADING', false);
      }
    },

    async fetchSessionMessages({ commit }, sessionId) {
      commit('SET_AUTOMATIONS_MESSAGES_LOADING', true);
      try {
        const { data } = await getGuardianMessagesApi(sessionId);
        const messages = (data?.messages || []).map(m => mapMessageToChatItem(m));
        commit('SET_ITEM_CHAT_HISTORY', { sessionId, chatHistory: messages });
      } catch (e) {
        console.error('fetchSessionMessages error:', e);
      } finally {
        commit('SET_AUTOMATIONS_MESSAGES_LOADING', false);
      }
    },

    async sendAutomationMessage({ commit, state }, { sessionId, content }) {
      commit('SET_AUTOMATIONS_SENDING', true);
      try {
        commit('APPEND_CHAT_MESSAGE', {
          sessionId,
          message: { type: 'provider', text: content },
        });

        const { data } = await sendGuardianMessageApi(sessionId, content);

        if (data?.agent_message) {
          commit('APPEND_CHAT_MESSAGE', {
            sessionId,
            message: { type: 'robot', text: data.agent_message.content || data.agent_message },
          });
        }

        if (data?.session_status === 'resolved') {
          commit('MOVE_TO_COMPLETED', { sessionId });
          commit('SET_EXPANDED_CARD', null);
          commit('SET_AUTOMATIONS_TOTAL_COUNT', state.automations.items.length);
        }

        notify({
          title: 'Success',
          message: 'Message sent successfully.',
          type: 'success',
        });
      } catch (e) {
        console.error('sendAutomationMessage error:', e);
        commit('APPEND_CHAT_MESSAGE', {
          sessionId,
          message: { type: 'error', text: 'Failed to send message. Please try again.' },
        });
        notify({
          title: 'Error',
          message: 'Failed to send message. Please try again.',
          type: 'error',
        });
      } finally {
        commit('SET_AUTOMATIONS_SENDING', false);
      }
    },

    async archiveAutomationSession({ commit }, sessionId) {
      try {
        await archiveGuardianSessionApi(sessionId);
        commit('REMOVE_AUTOMATION_ITEM', sessionId);
      } catch (e) {
        console.error('archiveAutomationSession error:', e);
      }
    },

    handleGuardianWebSocketEvent({ commit }, { event, payload }) {
      if (event === 'guardian_alert') {
        const item = mapSessionToItem({
          id: payload.sessionId,
          instance_id: payload.instanceId,
          workflow_name: payload.workflowName,
          patient_id: payload.patientId,
          patient_name: payload.patientName || '',
          last_message: payload.firstMessage,
          severity: payload.severity || 'high',
          created_at: payload.createdAt,
          status: 'waiting_for_provider',
        });
        commit('ADD_AUTOMATION_ITEM', item);
      } else if (event === 'guardian_message') {
        const msg = payload.message || {};
        commit('APPEND_CHAT_MESSAGE', {
          sessionId: payload.sessionId,
          message: { type: 'robot', text: msg.content || msg },
        });
        commit('UPDATE_ITEM_DESCRIPTION', {
          sessionId: payload.sessionId,
          description: msg.content || msg,
        });
      } else if (event === 'guardian_resolved') {
        commit('MOVE_TO_COMPLETED', { sessionId: payload.sessionId });
      }
    },

    async fetchApprovals({ commit }) {
      commit('SET_APPROVALS_LOADING', true);
      commit('SET_APPROVALS_ERROR', null);
      try {
        const { data } = await getPendingApprovalsApi();
        const items = (data?.items || []).map(mapApprovalToItem);
        commit('SET_APPROVALS_ITEMS', items);
      } catch (e) {
        console.error('fetchApprovals error:', e);
        commit('SET_APPROVALS_ERROR', 'Failed to load pending approvals.');
      } finally {
        commit('SET_APPROVALS_LOADING', false);
      }
    },

    async approveApproval({ commit, state }, { approvalExecutionId, comment }) {
      commit('SET_APPROVAL_ACTION_IN_FLIGHT', { id: approvalExecutionId, action: 'approve' });
      try {
        await approveApprovalApi(approvalExecutionId, comment);
        const item = state.approvals.items.find(i => i.id === approvalExecutionId);
        const approvedTitle = item?.nextNodeName
          ? `${item.nextNodeName} — Completed`
          : 'Action completed';
        commit('MOVE_APPROVAL_TO_APPROVED', { approvalExecutionId, approvedTitle });
        notify({ title: 'Success', message: 'Approval confirmed.', type: 'success' });
      } catch (e) {
        console.error('approveApproval error:', e);
      } finally {
        commit('SET_APPROVAL_ACTION_IN_FLIGHT', null);
      }
    },

    async rejectApproval({ commit }, { approvalExecutionId, comment }) {
      commit('SET_APPROVAL_ACTION_IN_FLIGHT', { id: approvalExecutionId, action: 'reject' });
      try {
        await rejectApprovalApi(approvalExecutionId, comment);
        commit('REMOVE_APPROVAL_ITEM', approvalExecutionId);
        notify({ title: 'Success', message: 'Approval cancelled.', type: 'success' });
      } catch (e) {
        console.error('rejectApproval error:', e);
      } finally {
        commit('SET_APPROVAL_ACTION_IN_FLIGHT', null);
      }
    },

    async fetchMentions({ commit, state, rootState, dispatch }) {
      commit('SET_MENTIONS_LOADING', true);
      commit('SET_MENTIONS_ERROR', null);
      try {
        // Ensure customer data is loaded before mapping mentions,
        // otherwise author initials/names will show as "?"
        const customers = rootState.myPatients?.practiceInfo?.customers;
        if (!customers || Object.keys(customers).length === 0) {
          const clinicId = rootState.login?.userInfo?.clinic_id;
          if (clinicId) {
            await dispatch('myPatients/practiceInfo/fetchCustomersByClinicId', clinicId, { root: true });
          }
        }

        const { data } = await getMentionsApi({
          page: state.mentions.page,
          perPage: state.mentions.perPage,
        });
        commit('SET_MENTIONS_DATA', data);
        commit('SET_MENTIONS_TOTAL', data.totalCount);
        if (data.unreadCount != null) {
          commit('SET_MENTIONS_UNREAD_COUNT', data.unreadCount);
        }
      } catch (e) {
        console.error('fetchMentions error:', e);
        commit('SET_MENTIONS_ERROR', 'Failed to load mentions. Please try again.');
      } finally {
        commit('SET_MENTIONS_LOADING', false);
      }
    },

    async markMentionAsRead({ commit, state }, noteId) {
      try {
        await markNoteAsReadApi(noteId);
        commit('MARK_MENTION_READ', noteId);
        if (state.mentions.unreadCount > 0) {
          commit('SET_MENTIONS_UNREAD_COUNT', state.mentions.unreadCount - 1);
        }
      } catch (e) {
        console.error('markMentionAsRead error:', e);
      }
    },

    async fetchMentionReplies(_, noteId) {
      const response = await getRepliesApi(noteId, { include_nested: false });
      return response?.data || [];
    },

    async createMentionReply(_, { noteId, content }) {
      await createReplyApi(noteId, { content });
    },

    async fetchTasks({ commit, state }, { silent = false } = {}) {
      if (!silent) {
        commit('SET_TASKS_LOADING', true);
      }
      commit('SET_TASKS_ERROR', null);
      try {
        const { data } = await getTasksApi({
          filter: state.tasks.filter,
          page: state.tasks.page,
          perPage: state.tasks.perPage,
        });
        commit('SET_TASKS_DATA', data);
        if (data.openedTaskCount != null) {
          commit('SET_TASKS_OPENED_COUNT', data.openedTaskCount);
        }
      } catch (e) {
        console.error('fetchTasks error:', e);
        commit('SET_TASKS_ERROR', 'Failed to load tasks. Please try again.');
      } finally {
        if (!silent) {
          commit('SET_TASKS_LOADING', false);
        }
      }
    },

    async fetchOrdersTabsBadge({ commit, state }) {
      try {
        const { data } = await getOrdersTabsApi({ patientId: state.orders.patientId || undefined });
        const badgeMap = (data.tabs || []).reduce((acc, tab) => {
          if (!tab?.id) return acc;
          acc[tab.id] = tab.badge || { unread: 0, total: 0 };
          return acc;
        }, {});
        commit('SET_ORDERS_TABS_BADGE', badgeMap);
        commit('SET_ORDERS_TOP_BADGE', data.ordersBadge);
        commit('SET_ORDERS_PARTIAL_ERRORS', data.partialErrors);
      } catch (e) {
        console.error('fetchOrdersTabsBadge error:', e);
      }
    },

    async fetchOrdersProviderOptions({ commit }) {
      try {
        const { data } = await getOrdersProviderOptionsApi();
        const nextOptions = Array.isArray(data) && data.length > 0
          ? data
          : [{ value: 'all', label: 'All' }];

        commit('SET_ORDERS_PROVIDER_OPTIONS', nextOptions);
      } catch (e) {
        console.error('fetchOrdersProviderOptions error:', e);
        commit('SET_ORDERS_PROVIDER_OPTIONS', [{ value: 'all', label: 'All' }]);
      }
    },

    async fetchOrdersIssueTypes({ commit, state }) {
      if (state.orders.issueTypeOptionsLoaded) return;
      try {
        const { data } = await getOrdersIssueTypesApi();
        const options = Array.isArray(data) && data.length > 0
          ? data
          : [{ value: 'all', label: 'All' }];
        commit('SET_ORDERS_ISSUE_TYPE_OPTIONS', options);
      } catch (e) {
        console.error('fetchOrdersIssueTypes error:', e);
        commit('SET_ORDERS_ISSUE_TYPE_OPTIONS', [{ value: 'all', label: 'All' }]);
      }
    },

    async fetchOrders({ commit, state, dispatch }, { withBadge = true } = {}) {
      commit('SET_ORDERS_LOADING', true);
      commit('SET_ORDERS_ERROR', null);
      try {
        if (!state.orders.providerOptionsLoaded) {
          await dispatch('fetchOrdersProviderOptions');
        }
        if (!state.orders.issueTypeOptionsLoaded) {
          await dispatch('fetchOrdersIssueTypes');
        }

        if (withBadge && !state.orders.isArchived) {
          await dispatch('fetchOrdersTabsBadge');
        }

        const apiParams = (() => {
          const subTab = state.orders.subTab;
          const type = state.orders.type;
          const p = {
            patientId: state.orders.patientId || undefined,
            cursor: state.orders.cursorHistory[state.orders.cursorIndex] || '',
            limit: state.orders.limit,
          };

          if (state.orders.provider && state.orders.provider !== 'all') {
            const selectedProvider = state.orders.providerOptions
              .find(option => option.value === state.orders.provider);
            p.providerName = selectedProvider?.label || state.orders.provider;
          }

          if (state.orders.isArchived) {
            if (type === 'issues') {
              p.tab = 'issues';
            } else if (type === 'reports') {
              p.tab = 'reports';
            } else if (type === 'orders') {
              p.tab = 'operations';
              p.operationType = 'order';
            } else if (type === 'shipping') {
              p.tab = 'operations';
              p.operationType = 'shipping';
            }
          } else {
            p.tab = subTab;
            if (subTab === 'operations') {
              if (type === 'orders') p.operationType = 'order';
              else if (type === 'shipping') p.operationType = 'shipping';
            } else if (subTab === 'issues') {
              if (type && type !== 'all') p.issueType = type;
            } else if (subTab === 'reports') {
              if (type && type !== 'all') p.statusList = [type];
            }
          }
          return p;
        })();

        const apiFn = state.orders.isArchived ? getArchivedOrdersApi : getOrdersApi;
        const { data } = await apiFn(apiParams);

        const totalCount = data.total != null
          ? data.total
          : state.orders.isArchived
            ? (data.items || []).length
            : state.orders.tabsBadge[state.orders.subTab]?.total;
        commit('SET_ORDERS_DATA', {
          items: data.items || [],
          total: Number.isFinite(totalCount) ? totalCount : (data.items || []).length,
        });
        commit('SET_ORDERS_HAS_MORE', data.hasMore);
        commit('SET_ORDERS_NEXT_CURSOR', data.nextCursor);
        commit('SET_ORDERS_PAGE_UNREAD', data.unread);
        commit('SET_ORDERS_PARTIAL_ERRORS', data.partialErrors);

        if (data.hasMore && data.nextCursor) {
          commit('PUSH_ORDERS_CURSOR', data.nextCursor);
        }

        // Batch-read unread items after 4s delay so user can see them first
        if (!state.orders.isArchived) {
          const unreadIds = (data.items || []).filter(i => !i.read).map(i => i.id);
          if (unreadIds.length > 0) {
            setTimeout(() => {
              batchReadOrdersApi(unreadIds)
                .then(() => {
                  unreadIds.forEach(id => commit('MARK_ORDER_ITEM_READ', id));
                  dispatch('fetchOrdersTabsBadge');
                })
                .catch(e => console.error('batchReadOrders error:', e));
            }, 4000);
          }
        }
      } catch (e) {
        console.error('fetchOrders error:', e);
        commit('SET_ORDERS_ERROR', 'Failed to load orders. Please try again.');
      } finally {
        commit('SET_ORDERS_LOADING', false);
      }
    },

    async markOrderAsRead({ commit, dispatch }, id) {
      try {
        await markOrderReadApi(id);
        commit('MARK_ORDER_ITEM_READ', id);
        await dispatch('fetchOrdersTabsBadge');
      } catch (e) {
        console.error('markOrderAsRead error:', e);
      }
    },

    async archiveOrderItem({ commit, dispatch }, id) {
      try {
        await archiveOrderApi(id);
        commit('REMOVE_ORDER_ITEM', id);
        await dispatch('fetchOrdersTabsBadge');
      } catch (e) {
        console.error('archiveOrderItem error:', e);
      }
    },

    async batchArchiveOrders({ dispatch }, ids) {
      try {
        await batchArchiveOrdersApi(ids);
        await dispatch('fetchOrders');
        await dispatch('fetchOrdersTabsBadge');
      } catch (e) {
        console.error('batchArchiveOrders error:', e);
      }
    },

    async batchUnarchiveOrders({ dispatch }, ids) {
      try {
        await batchUnarchiveOrdersApi(ids);
        await dispatch('fetchOrders');
        await dispatch('fetchOrdersTabsBadge');
      } catch (e) {
        console.error('batchUnarchiveOrders error:', e);
      }
    },

    setActiveTab({ commit, dispatch }, tab) {
      commit('SET_ACTIVE_TAB', tab);
      const fetchMap = {
        automations: 'fetchAutomations',
        mentions: 'fetchMentions',
        tasks: 'fetchTasks',
        orders: 'fetchOrders',
      };
      if (tab === 'orders') {
        commit('RESET_ORDERS_CURSOR');
        dispatch('fetchOrdersProviderOptions');
        dispatch('fetchOrdersIssueTypes');
      }
      if (fetchMap[tab]) dispatch(fetchMap[tab]);
      if (tab === 'automations') dispatch('fetchApprovals');
    },
  },
};

export default notificationsModule;
